import { useState } from "react"
import { ChevronDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  BRANCH_STORAGE_KEY,
  DEFAULT_BRANCH,
  HEADER_BRANCHES,
} from "./header.config"

type BranchSelectorProps = {
  lightForeground: boolean
}

const getStoredBranchId = () => {
  try {
    const storedId = window.localStorage.getItem(BRANCH_STORAGE_KEY)
    return HEADER_BRANCHES.some((branch) => branch.id === storedId)
      ? storedId!
      : DEFAULT_BRANCH.id
  } catch {
    return DEFAULT_BRANCH.id
  }
}

export const BranchSelector = ({ lightForeground }: BranchSelectorProps) => {
  const [selectedId, setSelectedId] = useState(getStoredBranchId)
  const selectedBranch =
    HEADER_BRANCHES.find((branch) => branch.id === selectedId) ?? DEFAULT_BRANCH

  const selectBranch = (branchId: string) => {
    if (!HEADER_BRANCHES.some((branch) => branch.id === branchId)) return
    setSelectedId(branchId)
    try {
      window.localStorage.setItem(BRANCH_STORAGE_KEY, branchId)
    } catch {
      // Selection still works for the current session when storage is unavailable.
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-layout="header-integrated"
          aria-label={`Chọn chi nhánh, hiện tại ${selectedBranch.name}`}
          className={cn(
            "group flex min-w-[104px] self-stretch items-center justify-end gap-1.5 border-l bg-transparent px-2 text-left whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-current sm:min-w-[156px] sm:justify-between sm:gap-2 sm:px-3 lg:min-w-[148px] xl:min-w-[172px] xl:px-4",
            lightForeground
              ? "border-white/30 text-white hover:bg-white/10 data-[state=open]:bg-white/15"
              : "border-[#bca88f]/55 text-[#40372f] hover:bg-[#8b6748]/5 data-[state=open]:bg-[#8b6748]/10",
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="hidden text-[9px] font-semibold uppercase tracking-[0.09em] opacity-70 sm:inline xl:text-[10px]">
              Chi nhánh
            </span>
            <span className="hidden opacity-45 sm:inline" aria-hidden="true">·</span>
            <span className="block max-w-[88px] truncate text-xs font-medium sm:max-w-[104px] sm:text-[12px] xl:max-w-[120px] xl:text-[13px]">
              {selectedBranch.name}
            </span>
          </span>
          <ChevronDown
            className="size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[240px] max-w-[calc(100vw-24px)] rounded-xl border-[#dfd2c2] bg-[#fffdf9] p-2 text-[#40372f] shadow-[0_16px_40px_rgba(67,52,39,0.18)]"
      >
        <DropdownMenuLabel className="px-3 pt-2.5 pb-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8b6748]">
            Chọn chi nhánh
          </span>
          <span className="mt-0.5 block text-xs font-normal text-[#77695c]">
            Không gian bạn muốn ghé
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mx-2 mb-1 bg-[#e8ded2]" />
        <DropdownMenuRadioGroup
          value={selectedId}
          onValueChange={selectBranch}
          className="flex flex-col gap-1"
        >
          {HEADER_BRANCHES.map((branch) => (
            <DropdownMenuRadioItem
              key={branch.id}
              value={branch.id}
              showIndicator={false}
              className="w-full rounded-lg px-3 py-2.5 text-[13px] text-[#51463c] transition-colors focus:bg-[#f1e8dc] focus:text-[#5c432e] data-[state=checked]:bg-[#e9dccb] data-[state=checked]:font-semibold data-[state=checked]:text-[#654a32]"
            >
              <span className="flex min-w-0 flex-1 items-center justify-between gap-4">
                <span className="truncate">{branch.name}</span>
                <span
                  className="w-8 shrink-0 text-right text-[11px] font-medium tracking-[0.08em] text-[#9a8b7c]"
                  aria-hidden="true"
                >
                  {branch.code}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
